import {useMemo} from 'react'
import {Empty, Space, Table, Typography} from 'antd'
import {DeleteOutlined, EditOutlined} from '@ant-design/icons'
import EllipsisTooltip from '../../common/EllipsisTooltip'
import RecordsCount from '../../common/RecordsCount'
import TableActions from '../../common/TableActions'
import {useIntl} from 'react-intl'
import {formatEmptyValue} from '../../../utils/common'

const SubSystemTable = ({
  dataSource,
  pagination = false,
  getSubSystemListData = null,
  onEdit = null,
  onDelete = null,
}) => {
  const intl = useIntl()

  const columns = useMemo(
    () => [
      {
        title: intl.formatMessage({ id: 'subSystem.subSystemId' }),
        dataIndex: 'sub_system_id',
        width: '20%',
        sorter: (a, b) =>
          (a.sub_system_id || '').localeCompare(b.sub_system_id || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {formatEmptyValue(text)}
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'subSystem.name' }),
        dataIndex: 'name',
        width: '20%',
        sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {formatEmptyValue(text)}
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'subSystem.systemId' }),
        dataIndex: ['system_data', 'system_id'],
        width: '15%',
        sorter: (a, b) =>
          (a.system_data?.system_id || '').localeCompare(
            b.system_data?.system_id || ''
          ),
        render: (_, record) => (
          <EllipsisTooltip className="ellipsis">
            {formatEmptyValue(record?.system_data?.system_id)}
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'subSystem.systemName' }),
        dataIndex: ['system_data', 'name'],
        width: '20%',
        sorter: (a, b) =>
          (a.system_data?.name || '').localeCompare(b.system_data?.name || ''),
        render: (_, record) => (
          <EllipsisTooltip className="ellipsis">
            {formatEmptyValue(record?.system_data?.name)}
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'subSystem.make' }),
        dataIndex: ['system_data', 'make'],
        width: '10%',
        sorter: (a, b) =>
          (a.system_data?.make || '').localeCompare(b.system_data?.make || ''),
        render: (_, record) => (
          <EllipsisTooltip className="ellipsis">
            {formatEmptyValue(record?.system_data?.make)}
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'subSystem.action' }),
        align: 'right',
        width: 135,
        render: (_, record) => (
          <TableActions
            actions={[
              {
                key: 'edit',
                label: intl.formatMessage({ id: 'forms.edit' }),
                icon: <EditOutlined />,
                onClick: () => onEdit(record),
              },
              {
                key: 'delete',
                label: intl.formatMessage({ id: 'forms.delete' }),
                icon: <DeleteOutlined />,
                danger: true,
                onClick: () => onDelete(record),
              },
            ]}
          />
        ),
      },
    ],
    [dataSource, pagination]
  )

  return (
    <Space
      direction="vertical"
      size="middle"
      style={{ width: '100%', verticalAlign: 'top' }}
    >
      <Table
        rowKey={(record) => `sub-system-${record.id}`}
        columns={columns}
        dataSource={dataSource}
        showSorterTooltip={false}
        scroll={{
          y: '70vh',
        }}
        pagination={
          pagination
            ? {
                current: pagination?.currentPage,
                total: pagination?.totalCount,
                pageSize: 10,
                showSizeChanger: false,
                hideOnSinglePage: true,
                showTotal: (total) => <RecordsCount count={total} />,
                onChange: (page) => {
                  const offset = (page - 1) * 10
                  const url = pagination?.nextPageLink
                    ? new URL(pagination?.nextPageLink)
                    : new URL(pagination?.prevPageLink)
                  url.searchParams.set('offset', offset)
                  getSubSystemListData(url.toString())
                },
              }
            : false
        }
        locale={{
          emptyText: (
            <div style={{ padding: '40px' }}>
              <Empty description={<RecordsCount count={0} />} />
            </div>
          ),
        }}
      />
      {pagination && pagination?.totalPages <= 1 && pagination?.totalCount > 0 && (
        <Typography.Text>
          <RecordsCount count={pagination?.totalCount} />
        </Typography.Text>
      )}
    </Space>
  )
}

export default SubSystemTable
